import { PageHeading } from '@src/components/PageHeading/PageHeading';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  Container,
  PageContent,
  Expandable,
  ExpandableGroup,
  Input,
  Prose,
  Shell,
  Stack,
  Typography,
} from '@src/components';
import { FAQ_SECTIONS, type FaqCopy, type FaqQuestion } from '@src/pages/FaqPage/questions';
import { isDotCom } from '@src/site';
import { openSupportDialog } from '@src/support/openSupportDialog';

function pickFaq<T extends ReactNode>(value: FaqCopy<T>, lang: string): T {
  return (lang === 'ru' ? value.ru : value.en) as T;
}

function matchesQuery(query: string, question: string, search = ''): boolean {
  const haystack = `${question} ${search}`.toLowerCase();
  return query
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((word) => haystack.includes(word));
}

function forSite(questions: readonly FaqQuestion[], com: boolean): FaqQuestion[] {
  return questions.filter((item) => {
    if (item.ruOnly) {
      return !com;
    }
    if (item.comOnly) {
      return com;
    }
    return true;
  });
}

function FaqAnswer({ children }: { children: ReactNode }) {
  if (typeof children === 'string') {
    return (
      <Prose>
        <p>{children}</p>
      </Prose>
    );
  }
  return <Prose>{children}</Prose>;
}

export function FaqPage() {
  const { t, i18n } = useTranslation();
  const [query, setQuery] = useState('');
  const com = isDotCom();
  const lang = i18n.language;

  const sections = useMemo(() => {
    const searching = query.trim().length > 0;
    const seen = new Set<string>();
    return FAQ_SECTIONS.map((section) => {
      const items = forSite(section.questions, com)
        .map((item) => ({
          id: item.question.ru,
          question: pickFaq(item.question, lang),
          answer: pickFaq(item.answer, lang),
          search: item.search,
        }))
        .filter((item) => {
          if (searching && !matchesQuery(query, item.question, item.search)) {
            return false;
          }
          if (searching) {
            if (seen.has(item.id)) {
              return false;
            }
            seen.add(item.id);
          }
          return true;
        });
      return { id: section.id, titleKey: section.titleKey, items };
    }).filter((section) => section.items.length > 0);
  }, [com, lang, query]);

  return (
    <Shell>
      <PageContent width="medium" testId="faq">
        <Stack gap={8} className="landscape-sky-content">
          <Stack gap={5} align="center" as="header" wide>
            <PageHeading title={<>{t('faq.title')}</>} />
            <Container width="measure">
              <Input
                type="search"
                name="faq-search"
                label={t('faq.search')}
                value={query}
                onValueChange={setQuery}
                autoComplete="off"
                testId="faq-search"
              />
            </Container>
          </Stack>

          {sections.length === 0 ? (
            <Typography as="p" role="body/lead">
              {t('faq.empty')}
            </Typography>
          ) : (
            sections.map((section) => (
              <Stack as="section" gap={4} key={section.id} testId={`faq-${section.id}`}>
                <Stack gap={4} wide>
                  <Typography as="h2" role="title/section">
                    {t(section.titleKey)}
                  </Typography>
                  <ExpandableGroup testId={`faq-${section.id}-list`}>
                    {section.items.map((item) => (
                      <Expandable icon={false} key={item.id} title={item.question}>
                        <FaqAnswer>{item.answer}</FaqAnswer>
                      </Expandable>
                    ))}
                  </ExpandableGroup>
                </Stack>
              </Stack>
            ))
          )}

          <Card
            tone="info"
            title={t('faq.moreTitle')}
            action={
              <Button variant="secondary" onClick={() => openSupportDialog()} testId="faq-write">
                {t('faq.moreButton')}
              </Button>
            }
          >
            {t('faq.moreHint')}
          </Card>
        </Stack>
      </PageContent>
    </Shell>
  );
}
